import React from 'react';
import { useTranslation } from 'react-i18next';
import { withSize } from 'react-sizeme';
import { makeStyles } from '@material-ui/core/styles';
import { Colors, Metrics } from '../../themes';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import clsx from 'clsx';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import SvgIcon from '../../icons/SvgIcon';
import Table from '../common/Table';
import DetailsTableHeader from '../common/DetailsTableHeader';

const useStyles = makeStyles((theme) => {
  return {
    DetailsWidget: {
      display: 'flex',
      flexDirection: 'column',
      overflowWrap: 'break-word',
    },
    headerButtonsContainer: {
      background: Colors.navBackgroundGrey,
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      borderRadius: '4px 4px 0 0',
      padding: 10,
      [theme.breakpoints.down(Metrics.minWidth)]: {
        padding: 6,
        justifyContent: 'flex-start',
      },
    },
    button: {
      margin: theme.spacing(2),
      fontSize: 14,
      color: Colors.white,
      border: `1px solid ${Colors.disabledGrey}`,
      '&:hover': {
        background: Colors.disabledGrey,
      },
    },
    smallSizeButton: {
      height: 35,
      fontSize: 12,
      paddingLeft: 6,
      paddingRight: 6,
    },
    saveButton: {
      background: Colors.blue,
      border: 'none',
      '&:hover': {
        background: Colors.lightBlue,
      },
      '&:disabled': {
        border: `1px solid ${Colors.disabledGrey}`,
        background: Colors.navBackgroundGrey,
      },
    },
    deleteButton: {
      margin: 10,
      marginRight: 'auto',
      minWidth: 38,
      width: 38,
      maxWidth: 38,
      padding: 0,
      borderColor: Colors.red,
      '&:hover': {
        background: Colors.red,
      },
      [theme.breakpoints.down(Metrics.minWidth)]: {
        marginRight: 8,
      },
    },
    // details
    detailsContainer: {
      padding: '25px 20px',
      [theme.breakpoints.down('xs')]: {
        padding: 15,
      },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    formContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
  };
});

const CustomerDetails = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { width } = props.size;
  const isSmall = width < Metrics.formDetails.small;
  const formInputWidth = isSmall ? width - 40 : (width - 60) / 2;
  const {
    deleteButtonText,
    add1ButtonText,
    onDeleteButtonClick,
    onAdd1ButtonClick,
    onSaveButtonClick,
    isDeleteButtonDisabled,
    saveButtonDisabled,
    onChange,
    hideDetails,
    onKeyPressFormSubmit,
  } = props;
  const smallerThanMedium = useMediaQuery(`(max-width: ${Metrics.breakpoints.md}px)`);
  const handleSubmit = (event) => {
    event.preventDefault();
    onSaveButtonClick();
  };
  return (
    <div className={classes.DetailsWidget}>
      <form
        id="detailsForm"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}
        onKeyPress={(event) => onKeyPressFormSubmit(event)}
      >
        <div className={classes.headerButtonsContainer}>
          <Button
            type="button"
            className={clsx(classes.deleteButton, isSmall && classes.smallSizeButton)}
            variant="outlined"
            aria-label={deleteButtonText}
            onClick={onDeleteButtonClick}
            disabled={isDeleteButtonDisabled}
          >
            <SvgIcon
              icon={'Trash'}
              color={isDeleteButtonDisabled ? Colors.disabledGrey : Colors.white}
              stroke={2}
              width={14}
            />
          </Button>
          <Button
            type="button"
            variant="outlined"
            className={clsx(classes.button, isSmall && classes.smallSizeButton)}
            onClick={onAdd1ButtonClick}
            endIcon={<SvgIcon icon="Add" width={smallerThanMedium ? 15 : 20} color={Colors.white} />}
          >
            {add1ButtonText}{' '}
          </Button>
          <Button
            type="submit"
            variant="contained"
            className={clsx(classes.button, classes.saveButton, isSmall && classes.smallSizeButton)}
            endIcon={
              <SvgIcon
                icon="Save"
                width={smallerThanMedium ? 15 : 20}
                color={saveButtonDisabled ? Colors.disabledGrey : Colors.white}
              />
            }
            disabled={saveButtonDisabled}
          >
            {t('Global_save')}
          </Button>
        </div>
        {!hideDetails && (
          <div className="bodyComponent">
            <div className={classes.detailsContainer}>
              <Typography variant="h2">{t('common.information').toUpperCase()}</Typography>
              <div className={classes.formContainer}>
                <TextField
                  style={{ width: formInputWidth }}
                  id="firstName"
                  label={`${t('common.name')} 1`}
                  name="firstName"
                  margin="normal"
                  value={props.firstName}
                  onChange={onChange}
                />
                <TextField
                  style={{ width: formInputWidth }}
                  id="lastName"
                  label={`${t('common.name')} 2`}
                  name="lastName"
                  margin="normal"
                  value={props.lastName}
                  onChange={onChange}
                />
                <TextField
                  style={{ width: formInputWidth }}
                  id="street1"
                  label={t('siteManager_siteInfo_streetAddress')}
                  name="street1"
                  margin="normal"
                  value={props.street1}
                  onChange={onChange}
                />
                <TextField
                  style={{ width: formInputWidth }}
                  id="street1no"
                  label={t('siteManager_siteInfo_streetNo')}
                  name="street1no"
                  margin="normal"
                  value={props.street1no}
                  onChange={onChange}
                />
                <TextField
                  style={{ width: formInputWidth }}
                  id="zip"
                  label={t('siteManager_siteInfo_zipCode')}
                  name="zip"
                  margin="normal"
                  value={props.zip}
                  onChange={onChange}
                />
                <TextField
                  style={{ width: formInputWidth }}
                  id="city"
                  label={t('siteManager_siteInfo_city')}
                  name="city"
                  margin="normal"
                  value={props.city}
                  onChange={onChange}
                />
                <TextField
                  style={{ width: formInputWidth }}
                  id="customerNumber"
                  label={t('customer.customerId')}
                  name="customerNumber"
                  margin="normal"
                  value={props.customerNumber}
                  onChange={onChange}
                />
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default withSize()(CustomerDetails);
